import { MessageSquare, Lightbulb, CheckCircle, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import Link from "next/link"

export function HowCareAdvisorWorks() {
  const steps = [
    {
      title: "1. Ask a Question",
      description: "Type your Minnesota compliance query in plain language. No need for legal terminology or complex phrasing.",
      icon: <MessageSquare className="h-10 w-10" />,
    },
    {
      title: "2. Get Answers",
      description:
        "Receive clear guidance with direct citations to Minnesota statutes and official government sources.",
      icon: <Lightbulb className="h-10 w-10" />,
    },
    {
      title: "3. Stay Compliant",
      description:
        "Apply insights with confidence, knowing they come from authoritative Minnesota regulatory requirements.",
      icon: <CheckCircle className="h-10 w-10" />,
    },
  ]

  return (
    <section className="relative py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white dark:from-[#1E293B] dark:to-[#0F172A] overflow-hidden">
  
      
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Gradient orbs */}
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-primary/5 dark:bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-primary-light/5 dark:bg-primary-light/10 rounded-full blur-3xl"></div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-grid-gray-500/[0.02] dark:bg-grid-gray-400/[0.03] bg-[length:30px_30px]"></div>
      </div>

      <div className="container px-4 md:px-8 lg:px-12 mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
          <div className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light mb-4 backdrop-blur-sm">
            HOW CAREADVISOR WORKS
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground dark:text-white mb-6 tracking-tight">
            Your <span className="text-primary dark:text-primary-light">Compliance</span> Process
          </h2>
          <p className="text-xl text-muted-foreground dark:text-gray-300 max-w-3xl mx-auto">
            CareAdvisor simplifies Minnesota compliance with a straightforward process designed to give you official regulatory answers quickly.
          </p>
        </div>

        {/* Step cards with glowing borders and gradients */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto relative mb-20">
          {/* Connector lines between steps (hidden on mobile) */}
          <div className="hidden md:block absolute top-28 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-0.5">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/40 to-primary-light/40 dark:from-primary/60 dark:to-primary-light/60"></div>
          </div>

          {steps.map((step, index) => (
            <div
              key={index}
              className="relative group"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Outer glow effect */}
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-primary/20 to-primary-light/20 dark:from-primary/30 dark:to-primary-light/30 opacity-0 group-hover:opacity-100 transition-all duration-500 blur-md"></div>
              
              {/* Card */}
              <div className="relative bg-white dark:bg-[#1E293B] rounded-xl shadow-xl border border-gray-100 dark:border-gray-800/50 p-8 transition-all duration-300 group-hover:translate-y-[-8px]">
                {/* Step number accent */}
                <div className="absolute top-0 right-0 mt-6 mr-6 text-xl font-bold opacity-10 dark:opacity-20 text-primary dark:text-primary-light">
                  {index + 1}
                </div>
                
                {/* Icon with gradient circle background */}
                <div className="w-20 h-20 rounded-2xl mb-6 bg-gradient-to-br from-primary/10 to-primary-light/10 dark:from-primary/20 dark:to-primary-light/20 flex items-center justify-center text-primary dark:text-primary-light">
                  {step.icon}
                </div>
                
                {/* Content */}
                <h3 className="text-2xl font-bold text-foreground dark:text-white mb-4">{step.title}</h3>
                <p className="text-muted-foreground dark:text-gray-300 mb-6">{step.description}</p>
                
                {/* Decorative accent line */}
                <div className="h-1 w-12 bg-gradient-to-r from-primary to-primary-light rounded-full transition-all duration-300 group-hover:w-20"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Example interaction card */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="relative">
            {/* Subtle glow effect */}
            <div className="absolute -inset-4 rounded-2xl bg-gradient-to-r from-primary/10 to-primary-light/10 dark:from-primary/20 dark:to-primary-light/20 blur-xl"></div>
            
            <div className="relative rounded-xl overflow-hidden bg-white dark:bg-[#131E31] border border-gray-100 dark:border-gray-800/50 shadow-2xl">
              {/* Example header */}
              <div className="bg-gray-50 dark:bg-[#1E293B] px-6 py-4 border-b border-gray-100 dark:border-gray-800/50">
                <div className="flex items-center gap-3">
                  <div className="flex space-x-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">CareAdvisor Compliance Assistant</div>
                </div>
              </div>
              
              {/* Chat interface */}
              <div className="p-6 space-y-4">
                {/* User question */}
                <div className="flex justify-end">
                  <div className="bg-primary/10 dark:bg-primary/20 rounded-2xl rounded-tr-none px-4 py-3 text-foreground dark:text-white max-w-md">
                    What are the staff training requirements for assisted living facilities in Minnesota?
                  </div>
                </div>
                
                {/* System response */}
                <div className="flex">
                  <div className="bg-gray-100 dark:bg-gray-800/50 rounded-2xl rounded-tl-none px-4 py-3 text-foreground dark:text-gray-200 max-w-md">
                    <p className="mb-2">According to Minnesota Statute 144G.60, all staff members of assisted living facilities must complete:</p>
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                      <li>Initial orientation before providing direct care</li>
                      <li>12 hours of annual continuing education</li>
                      <li>Dementia care training for specialty facilities</li>
                    </ul>
                    <div className="mt-2 text-xs text-primary dark:text-primary-light">Source: MN Statute 144G.60, Subdivision, 4</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center max-w-xl mx-auto pb-2">
          <Button asChild className="relative overflow-hidden rounded-full px-8 py-6 bg-primary hover:bg-primary/90 text-white group">
            <Link href="/register">
              <span className="relative z-10 flex items-center gap-2 text-base font-medium">
                See CareAdvisor in Action
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
              
              {/* Animated shine effect */}
              <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
            </Link>
          </Button>
        </div>
      </div>
      
     
    </section>
  )
} 